import {
  getActiveDailyReportsByCategory,
  getCollectorReport,
  getCollectingCompaniesByCategory,
  getCompanyLocationDetails,
  getNonHandoverDailyReports,
  getOperationTypesOptions,
  getCurrentLocationInfo
} from 'components/waste-management/api'
import {
  DangerousDEEEPayloadItem,
  EditCreateModalProps,
  NonDangerousDEEEPayloadItem
} from 'components/waste-management/interfaces'
import { useNotification } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { handleQueryError } from 'utils/helpers'
import { Modal } from 'components/core-ui/Modal'
import { Grid } from '@mui/material'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Alert, Button, RadioButtonGroup } from '@fullscreendigital/carrefour-dex-fe'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { AsyncAutocompleteOption } from 'components/core-ui/AsyncAutocomplete/AsyncAutocompleteDefinitions'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import { getUserStoreInfo } from 'utils/authService'
import { DangerousDEEEReportForm } from 'components/waste-management/common/DangerousDEEEReportForm'
import { NonDangerousDEEEReportForm } from 'components/waste-management/common/NonDangerousDEEEReportForm'
import { getInitialValues, getValidationSchema } from 'components/waste-management/common/helpers'
import {
  toCurrentCompany,
  toDailyReportsOptions,
  toWasteReportPayload
} from 'components/waste-management/common/serializers'
import useExpeditorAuthorizationData from 'components/waste-management/common/hooks/useExpeditorAuthorizationData'

const CollectorReportModal = (props: EditCreateModalProps) => {
  const { isModalOpen, closeModal, isEdit, editItemId, shouldRefetchItem, setShouldRefreshData } =
    props

  const [currentStore, setCurrentStore] = useState(null)
  const [dailyReportsOptions, setDailyReportsOptions] = useState<AsyncAutocompleteOption[]>([])
  const [isDangerousDEEE, setIsDangerousDEEE] = useState(false)
  const [currentCompany, setCurrentCompany] = useState(null)

  const { showNotification } = useNotification()

  const { getAuthorizationData, setAuthorizationData } = useExpeditorAuthorizationData()

  const expeditorAuthorizationData = useMemo(() => {
    if (isModalOpen) {
      return getAuthorizationData()
    } else {
      return {
        expeditorEnvironmentAuthorizationNumber: '',
        expeditorEnvironmentAuthorizationExpirationDate: ''
      }
    }
  }, [isModalOpen])

  const { data: editItem } = getCollectorReport(showNotification, editItemId, shouldRefetchItem)

  const initialValues = useMemo(() => getInitialValues(editItem), [editItem])

  const validationSchema = useMemo(
    () => getValidationSchema(isDangerousDEEE),
    [editItem, isDangerousDEEE]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(
        toWasteReportPayload(
          {
            ...values,
            measuredWeight: +values.measuredWeight,
            carrierHandoverDate: new Date(
              typeof values.carrierHandoverDate === 'string'
                ? values.carrierHandoverDate
                : values.carrierHandoverDate.toDate()
            ),
            downloadDate: values.downloadDate ? new Date(values.downloadDate) : null,
            carrierReceivedWeight: values.carrierReceivedWeight
              ? +values.carrierReceivedWeight
              : null,
            companyId: null
          },
          isDangerousDEEE
        )
      )

      if (values.expeditorEnvironmentAuthorizationNumber !== undefined) {
        setAuthorizationData({
          expeditorEnvironmentAuthorizationNumber: values.expeditorEnvironmentAuthorizationNumber,
          expeditorEnvironmentAuthorizationExpirationDate:
            values.expeditorEnvironmentAuthorizationExpirationDate
        })
      }
    }
  })

  useEffect(() => {
    setIsDangerousDEEE(formik.values.wasteType === 'HAZARDOUS')
  }, [formik.values.wasteType])

  useEffect(() => {
    setCurrentStore(getUserStoreInfo())
  }, [])

  useEffect(() => {
    formik.setFieldValue(
      'expeditorEnvironmentAuthorizationNumber',
      expeditorAuthorizationData.expeditorEnvironmentAuthorizationNumber
    )
    formik.setFieldValue(
      'expeditorEnvironmentAuthorizationExpirationDate',
      expeditorAuthorizationData.expeditorEnvironmentAuthorizationExpirationDate
    )
  }, [expeditorAuthorizationData])

  const { data: currentCollectingCompanies } = getCompanyLocationDetails(showNotification)

  const collectingCompanyOptions = useMemo(() => {
    return (
      currentCollectingCompanies?.list?.map(company => ({
        label: company.name,
        value: company.id
      })) || []
    )
  }, [currentCollectingCompanies])

  const { data: dailyReports } = getActiveDailyReportsByCategory(
    showNotification,
    formik.values.wasteCategory,
    isEdit
  )

  const { data: operationTypesOptions } = getOperationTypesOptions(showNotification)

  const { data: currentLocationInfo } = getCurrentLocationInfo(showNotification)

  const collectingCompanyValue = (formik.values as any).collectingCompany as
    | AsyncAutocompleteOption
    | undefined

  const { data: nonHandoverDailyReports } = getNonHandoverDailyReports(
    showNotification,
    !isEdit ? collectingCompanyValue?.label || '' : ''
  )

  const nonHandoverDailyReportOptions = useMemo(() => {
    const reports = isEdit ? editItem?.dailyReports : nonHandoverDailyReports
    if (!reports) return []
    const seen = new Set<number>()
    return reports.reduce((acc, report) => {
      if (!seen.has(report.id)) {
        seen.add(report.id)
        acc.push({ label: report.reportNo, value: report.id })
      }
      return acc
    }, [] as AsyncAutocompleteOption[])
  }, [isEdit, editItem, nonHandoverDailyReports])

  const nonHandoverCategoryOptions = useMemo(() => {
    const reports = isEdit ? editItem?.dailyReports : nonHandoverDailyReports
    if (!reports) return []
    const seen = new Set<string>()
    return reports.reduce((acc, report) => {
      report.wasteCategoryDailyReports?.forEach(cat => {
        if (!seen.has(cat.wasteCategoryName)) {
          seen.add(cat.wasteCategoryName)
          acc.push({ label: cat.wasteCategoryName, value: cat.id })
        }
      })
      return acc
    }, [] as AsyncAutocompleteOption[])
  }, [isEdit, editItem, nonHandoverDailyReports])

  useEffect(() => {
    if (collectingCompanyValue?.value && currentCollectingCompanies?.list) {
      const selectedCompany = currentCollectingCompanies.list.find(
        c => c.id === collectingCompanyValue.value
      )
      if (selectedCompany) {
        setCurrentCompany(toCurrentCompany(selectedCompany))
      }
    }
  }, [collectingCompanyValue, currentCollectingCompanies])

  useEffect(() => {
    if (dailyReports) {
      const dailyReportsOptions = toDailyReportsOptions(dailyReports.dailyReports)
      setDailyReportsOptions(dailyReportsOptions)
      if (!isEdit) {
        formik.setFieldValue('dailyReports', dailyReportsOptions)
        formik.setFieldValue('measuredWeight', dailyReports.totalMeasuredWeight)
      }
    }
  }, [dailyReports])

  useEffect(() => {
    if (currentCompany) {
      formik.setFieldValue('wasteCompanyName', currentCompany.companyName)
      formik.setFieldValue('environmentalAuthorizationNo', currentCompany.environmentalPermitNumber)
      formik.setFieldValue(
        'environmentalPermitExpirationDate',
        currentCompany.environmentalPermitExpirationDate
      )
    }
  }, [currentCompany])

  const mutateReport = async (values: DangerousDEEEPayloadItem | NonDangerousDEEEPayloadItem) => {
    editItem
      ? await makeCorePutRequest(
          `/waste-management-system/waste-report-collection-company/${editItem.id}`,
          values
        )
      : await makeCorePostRequest(
          '/waste-management-system/waste-report-collection-company',
          values
        )
  }

  const { mutate } = useMutation(mutateReport, {
    onSuccess: res => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: `Raportul a fost ${editItem?.id ? 'editat' : 'creat'} cu succes.`,
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        `A aparut o eroare la ${editItem?.id ? 'editarea raportului.' : 'adaugarea raportului.'}`,
        showNotification
      )
  })

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle={isEdit ? 'Editeaza proces verbal' : 'Adauga proces verbal'}
      modalWidth="50%"
    >
      <Grid container spacing={4}>
        <Grid xs={12} item>
          <FormInfoItems
            items={[
              {
                label: 'Locatie:',
                name: currentStore?.storeName,
                itemStyle: { gridTemplateColumns: '60px 1fr' }
              }
            ]}
          />
        </Grid>
        {isEdit && (
          <Grid xs={12} item>
            <Alert severity="warning">
              Companie colectoare nu se poate modifica. Pentru a selecta o companie colectoare
              diferita trebuie generat alt raport.
            </Alert>
          </Grid>
        )}
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="collectingCompany"
            label="Companie colectoare"
            value={collectingCompanyValue || { label: '', value: '' }}
            options={collectingCompanyOptions}
            onChange={value => formik.setFieldValue('collectingCompany', value)}
            error={!!(formik.errors as any).collectingCompany}
            helperText={(formik.errors as any).collectingCompany?.value as string}
            required
            disabled={isEdit}
          />
        </Grid>
        <Grid xs={12} md={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="dailyReports"
            label="Rapoarte zilnice"
            value={nonHandoverDailyReportOptions}
            options={nonHandoverDailyReportOptions}
            onChange={() => {}}
            multiple
            disabled
          />
        </Grid>
        <Grid xs={12} md={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="categories"
            label="Categorii"
            value={nonHandoverCategoryOptions}
            options={nonHandoverCategoryOptions}
            onChange={() => {}}
            multiple
            disabled
          />
        </Grid>
        <Grid xs={12} item>
          <RadioButtonGroup
            label="Tip deseu"
            dotStyle
            name="wasteType"
            onChange={(e, value) => formik.setFieldValue('wasteType', value)}
            value={formik.values.wasteType}
            defaultValue={formik.values.wasteType}
            row
            disabled={isEdit}
            options={[
              { label: 'Anexa 2', value: 'HAZARDOUS' },
              { label: 'Anexa 3', value: 'NONHAZARDOUS' }
            ]}
          />
        </Grid>
        {formik.values.wasteType === 'HAZARDOUS' ? (
          <DangerousDEEEReportForm
            formik={formik}
            currentCompany={currentCompany}
            operationTypesOptions={operationTypesOptions}
            currentLocationInfo={currentLocationInfo}
          />
        ) : (
          <NonDangerousDEEEReportForm
            formik={formik}
            currentCompany={currentCompany}
            operationTypesOptions={operationTypesOptions}
            currentLocationInfo={currentLocationInfo}
          />
        )}
        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={cancelModal} />
          <Button
            variant="contained"
            type="submit"
            label="Salveaza"
            onClick={() => formik.handleSubmit()}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default CollectorReportModal
